import Soundfont, { InstrumentName, Player } from 'soundfont-player';
import appSettings from '../appsettings.json';

// Import the wav files
import kickWav from '../assets/audio/kick.wav';
import snareWav from '../assets/audio/snare.wav';
import closedHatWav from '../assets/audio/closed-hat.wav';
import openHatWav from '../assets/audio/open-hat.wav';
import crashWav from '../assets/audio/crash-cymbal.wav';
import rideWav from '../assets/audio/ride-cymbal.wav';
import floorTomWav from '../assets/audio/floor-tom.wav';
import { MidiMeasure, MidiNote, Tempo } from './music-objects';

export type MidiPlayerTrack = {
	measures: MidiMeasure[];
	instrumentName: InstrumentName; // instrument name to use
	trackId: string;              // unique identifier for the track
	isPercussion: boolean;
};

type StopCallback = () => void;
type PlayLoopCallback = (playTimeMS: number, measureNumber: number) => Promise<void>;
type RemoveTrackCallback = (trackId: string) => void;
type LoadingInstrumentCallback = (instrumentName: string, loaded: boolean) => void;

const noteBounceOffset = 8;

// =============================================================================
// IndexedDB Helpers for Caching Instrument Files
// =============================================================================

function openIndexedDB(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open("SoundfontCacheDB", 1);
		request.onupgradeneeded = function () {
			const db = request.result;
			if (!db.objectStoreNames.contains("instruments")) {
				db.createObjectStore("instruments");
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

async function getFromIndexedDB(key: string): Promise<string | null> {
	try {
		const db = await openIndexedDB();
		return new Promise((resolve, reject) => {
			const tx = db.transaction("instruments", "readonly");
			const store = tx.objectStore("instruments");
			const request = store.get(key);
			request.onsuccess = () => resolve(request.result || null);
			request.onerror = () => reject(request.error);
		});
	} catch (e) {
		console.warn("IndexedDB error:", e);
		return null;
	}
}

async function putToIndexedDB(key: string, value: string): Promise<void> {
	const db = await openIndexedDB();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("instruments", "readwrite");
		const store = tx.objectStore("instruments");
		const request = store.put(value, key);
		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
	});
}

/**
 * Retrieves the instrument file text from cache (or fetches it if missing).
 * Now accepts a soundfont folder parameter.
 */
async function getCachedInstrumentContent(instrumentName: string, sf: string, format: string): Promise<string> {
	const url = `${appSettings.SoundfontBucketBaseUrl}${instrumentName}-${format}.js`;
	const cacheKey = `soundfont-${sf}-${instrumentName}-${format}`;
	console.log("Loading instrument from:", url);
	let cached = await getFromIndexedDB(cacheKey);
	if (cached) {
		console.log("Instrument found in cache:", cacheKey);
		return cached;
	}
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP error ${response.status}`);
		}
		cached = await response.text();
		try {
			await putToIndexedDB(cacheKey, cached);
		} catch (e) {
			console.warn("Failed to cache instrument in IndexedDB:", e);
		}
		console.log("Fetched and cached instrument:", url);
		return cached;
	} catch (e) {
		console.error("Failed to fetch instrument:", e);
		throw new Error("Network Error: Unable to fetch instrument file " + url);
	}
}

// =============================================================================
// Fake XMLHttpRequest for Custom Cached URLs
// =============================================================================

/**
 * This fake XHR implements minimal properties for URLs ending with "-cached.js".
 */
class FakeXMLHttpRequest {
	public onload: ((this: XMLHttpRequest, ev: Event) => any) | null = null;
	public onerror: ((this: XMLHttpRequest, ev: Event) => any) | null = null;
	public readyState: number = 0;
	public status: number = 0;
	public responseText: string = "";
	public response: string = "";
	private _url: string = "";

	open(_: string, url: string, __: boolean = true, ___?: string, ____?: string): void {
		this._url = url;
		this.readyState = 1; // OPENED
	}

	send(_?: Document | XMLHttpRequestBodyInit | null): void {
		// Our custom URL is expected to be of the form:
		//   {SoundfontBucketBaseUrl}{sf}/{instrumentName}-cached.js
		if (this._url.endsWith("-cached.js")) {
			const regex = new RegExp(
				appSettings.SoundfontBucketBaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + "([^/]+)/(.+)-cached\\.js"
			);
			const match = this._url.match(regex);
			if (match && match[1] && match[2]) {
				const sf = match[1];
				const instrumentName = match[2];
				getCachedInstrumentContent(instrumentName, sf, 'mp3')
					.then(cachedText => {
						this.status = 200;
						this.responseText = cachedText;
						this.response = cachedText; // Ensure response is set
						this.readyState = 4;
						if (this.onload) {
							const progressEvent = new ProgressEvent('load', { lengthComputable: false, loaded: cachedText.length, total: cachedText.length });
							this.onload.call(this as any, progressEvent);
						}
					})
					.catch(err => {
						console.error("FakeXHR error for instrument", instrumentName, "with soundfont", sf, ":", err);
						this.status = 500;
						this.readyState = 4;
						if (this.onerror) {
							const errorEvent = new ProgressEvent('error', { lengthComputable: false, loaded: 0, total: 0 });
							this.onerror.call(this as any, errorEvent);
						}
					});
			} else {
				console.error("URL format did not match expected pattern:", this._url);
				this.status = 400;
				this.readyState = 4;
				if (this.onerror) {
					const errorEvent = new ProgressEvent('error', { lengthComputable: false, loaded: 0, total: 0 });
					this.onerror.call(this as any, errorEvent);
				}
			}
			return;
		}
		throw new Error("FakeXHR used with non-cached URL");
	}

	getAllResponseHeaders(): string {
		return "";
	}
	overrideMimeType(_: string): void {
		// No-op
	}
}

// =============================================================================
// Patched XMLHttpRequest: Fake for Cached URLs, Native Otherwise
// =============================================================================

class PatchedXMLHttpRequest {
	private _impl: XMLHttpRequest | FakeXMLHttpRequest;

	constructor() {
		// We'll select the implementation later in open().
		this._impl = {} as any;
	}

	open(method: string, url: string, async: boolean = true, user?: string, password?: string): void {
		if (url.endsWith("-cached.js")) {
			this._impl = new FakeXMLHttpRequest();
		} else {
			this._impl = new OriginalXMLHttpRequest();
		}
		this._impl.open(method, url, async, user, password);
	}

	send(body?: Document | XMLHttpRequestBodyInit | null): void {
		this._impl.send(body);
	}

	set onload(handler: ((this: XMLHttpRequest, ev: Event) => any) | null) {
		(this._impl as any).onload = handler;
	}
	get onload(): ((this: XMLHttpRequest, ev: Event) => any) | null {
		return (this._impl as any).onload;
	}
	set onerror(handler: ((this: XMLHttpRequest, ev: Event) => any) | null) {
		(this._impl as any).onerror = handler;
	}
	get onerror(): ((this: XMLHttpRequest, ev: Event) => any) | null {
		return (this._impl as any).onerror;
	}
	get readyState(): number {
		return this._impl.readyState;
	}
	get status(): number {
		return this._impl.status;
	}
	get response(): any {
		return (this._impl as any).response;
	}
	get responseText(): string {
		return (this._impl as any).responseText;
	}
	getAllResponseHeaders(): string {
		return this._impl.getAllResponseHeaders();
	}
	overrideMimeType(mime: string): void {
		this._impl.overrideMimeType(mime);
	}
}

const OriginalXMLHttpRequest = window.XMLHttpRequest;
window.XMLHttpRequest = PatchedXMLHttpRequest as any;

// =============================================================================
// MidiPlayer Class
// =============================================================================

class MidiPlayer {
	private static playLoopCallback: PlayLoopCallback;
	private static stopCallback: StopCallback;
	private static removeTrackCallback: RemoveTrackCallback;
	private static loadingInstrumentCallback: LoadingInstrumentCallback;
	private static instrumentPlayers: Record<string, Player> = {};
	private static isPlaying: boolean = false;
	private static tracks: MidiPlayerTrack[] = [];
	private static audioContext: AudioContext = new AudioContext();
	private static tempo: Tempo;
	private static intervalId: any;
	private static playingNoteIds: Set<string> = new Set<string>();
	private static instrumentLoadingPromises: Map<string, Promise<void>> = new Map();
	private static sessionId: number = 0; // Incremented on stop to cancel pending loads

	private static kickBuffer: AudioBuffer | null = null;
	private static snareBuffer: AudioBuffer | null = null;
	private static closedHatBuffer: AudioBuffer | null = null;
	private static openHatBuffer: AudioBuffer | null = null;
	private static crashBuffer: AudioBuffer | null = null;
	private static rideBuffer: AudioBuffer | null = null;
	private static floorTomBuffer: AudioBuffer | null = null;

	static async initialize() {
		let response = await fetch(kickWav);
		let arrayBuffer = await response.arrayBuffer();
		MidiPlayer.kickBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(snareWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.snareBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(closedHatWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.closedHatBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(openHatWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.openHatBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(crashWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.crashBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(rideWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.rideBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);

		response = await fetch(floorTomWav);
		arrayBuffer = await response.arrayBuffer();
		MidiPlayer.floorTomBuffer = await MidiPlayer.audioContext.decodeAudioData(arrayBuffer);
	}

	static setTempo(tempo: Tempo) {
		MidiPlayer.tempo = tempo;
	}

	static setLoadingInstrumentCallback(loadingInstrumentCallback: LoadingInstrumentCallback) {
		MidiPlayer.loadingInstrumentCallback = loadingInstrumentCallback;
	}

	static setCallbacks(playLoopCallback: PlayLoopCallback, stopCallback: StopCallback, removeTrackCallback: RemoveTrackCallback) {
		MidiPlayer.playLoopCallback = playLoopCallback;
		MidiPlayer.stopCallback = stopCallback;
		MidiPlayer.removeTrackCallback = removeTrackCallback;
	}

	private static noteTimeoutsByNoteId: Map<string, number> = new Map<string, number>();
	static async play(startMeasureIndex: number = 0, finalMeasureIndex: number = 99) {
		if (MidiPlayer.isPlaying) {
			MidiPlayer.stop();
		}
		MidiPlayer.isPlaying = true;
		MidiPlayer.tracks = MidiPlayer.tracks.filter(track => track.measures.length >= startMeasureIndex);
		const shortestNoteDurationAllowed = 0.1; // seconds
		const updateInterval = 20; // ms

		if (MidiPlayer.tempo == null) {
			MidiPlayer.tempo = Tempo.DEFAULT_TEMPO;
		}
		const secondsPerBeat = 60 / MidiPlayer.tempo.getBpm();
		const playedNoteIds: Set<string> = new Set<string>();
		let lastUpdateTimeMS = Date.now();
		const referenceTrack: MidiPlayerTrack | null = MidiPlayer.tracks.length > 0
			? MidiPlayer.tracks.reduce((a, b) => (a.measures.length > b.measures.length ? a : b))
			: null;
		let beatsPerMeasure = referenceTrack?.measures[0]?.timeSignature.beats ?? 4;
		let currentMeasureLengthMS = secondsPerBeat * 1000 * beatsPerMeasure;
		let currentMeasureElapsedMS = 0;
		let currentMeasureNumber = startMeasureIndex;

		MidiPlayer.intervalId = setInterval(() => {
			const now = Date.now();
			currentMeasureElapsedMS += now - lastUpdateTimeMS;
			lastUpdateTimeMS = now;
			if (currentMeasureElapsedMS >= currentMeasureLengthMS) {
				currentMeasureElapsedMS -= currentMeasureLengthMS;
				beatsPerMeasure = referenceTrack?.measures[currentMeasureNumber]?.timeSignature.beats ?? 4;
				currentMeasureLengthMS = secondsPerBeat * 1000 * beatsPerMeasure;
				currentMeasureNumber++;
			}
			requestAnimationFrame(() => MidiPlayer.playLoopCallback(currentMeasureNumber, currentMeasureElapsedMS / currentMeasureLengthMS));
			if (currentMeasureNumber > finalMeasureIndex) {
				MidiPlayer.stop();
			} else {
				MidiPlayer.tracks.forEach(track => {
					const handleLiveTrack = async () => {
						const activeNotes: MidiNote[] = track.measures[currentMeasureNumber]?.notes.filter(note => {
							const noteStartTime = note.startBeat * secondsPerBeat * 1000;
							let timeDuration = note.getBeatDuration() * secondsPerBeat * 1000;
							if (timeDuration < shortestNoteDurationAllowed) {
								timeDuration = shortestNoteDurationAllowed;
							}
							const errorMargin = updateInterval * 2;
							return (currentMeasureElapsedMS >= noteStartTime && currentMeasureElapsedMS <= noteStartTime + timeDuration + errorMargin);
						});
						const player = track.isPercussion ? null : MidiPlayer.instrumentPlayers[track.instrumentName];
						activeNotes?.forEach(note => {
							if (!playedNoteIds.has(note.id)) {
								playedNoteIds.add(note.id);
								MidiPlayer.playingNoteIds.add(note.id);
								let duration = note.getBeatDuration() * secondsPerBeat;
								if (track.isPercussion) {
									duration = Math.min(duration, 0.5 * secondsPerBeat);
								}
								if (duration < shortestNoteDurationAllowed) {
									duration = shortestNoteDurationAllowed;
								}
								if (track.isPercussion) {
									MidiPlayer.playDrumSound(note);
								} else {
									player?.play(note.noteName, undefined, { duration, gain: note.velocity });
								}
								const highlightPlayingnotes = async () => {
									const noteElements = document.querySelectorAll(`.note-${note.id}`);
									noteElements.forEach((element: Element) => {
										const elementY = element.getAttribute('y');
										if (elementY) {
											const y = parseFloat(elementY);
											element.setAttribute('y', (y + noteBounceOffset).toString());
										}
										element.setAttribute('fill', 'red');
									});

									// Clear the timeout if it exists
									const existingTimeoutId = MidiPlayer.noteTimeoutsByNoteId.get(note.id);
									if (existingTimeoutId) {
										clearTimeout(existingTimeoutId);
									}

									// Set a new timeout for the note to stop playing
									MidiPlayer.noteTimeoutsByNoteId.set(note.id, setTimeout(() => {
										if (MidiPlayer.playingNoteIds.has(note.id)) {
											MidiPlayer.playingNoteIds.delete(note.id);
											// Find the last playing note (last duration playing)
											let lastMeasureWithNotes: MidiMeasure | null = null
											for (let i = track.measures.length - 1; i >= 0; i--) {
												if (track.measures[i].notes.length > 0) {
													lastMeasureWithNotes = track.measures[i];
													break;
												}
											}
											if (lastMeasureWithNotes !== null) {
												const lastNote = lastMeasureWithNotes.notes.reduce((a, b) => {
													return a.startBeat + a.getBeatDuration() > b.startBeat + b.getBeatDuration() ? a : b;
												});
												if (lastNote.id === note.id) {
													MidiPlayer.removeTrack(track.trackId);
												}
											}
											// Clear the timeout for the note
											MidiPlayer.noteTimeoutsByNoteId.delete(note.id);
											noteElements.forEach((element: Element) => {
												const elementY = element.getAttribute('y');
												if (elementY) {
													const y = parseFloat(elementY);
													element.setAttribute('y', (y - noteBounceOffset).toString());
												}
												element.setAttribute('fill', 'black');
											});
										}
									}, duration * 1000));
								};
								highlightPlayingnotes(); // Highlight the note elements asynchronously
							}
						});
					};
					handleLiveTrack(); // Handle the live track asynchronously
				});
			}
		}, updateInterval);
	}

	static async addTrack(track: MidiPlayerTrack): Promise<boolean> {
		const mySessionId = MidiPlayer.sessionId;
		if (!track.measures || track.measures.filter(measure => measure.notes.length > 0).length <= 0) {
			return true;
		}
		if (!track.isPercussion && !MidiPlayer.instrumentPlayers[track.instrumentName]) {
			if (MidiPlayer.instrumentLoadingPromises.has(track.instrumentName)) {
				await MidiPlayer.instrumentLoadingPromises.get(track.instrumentName);
			} else {
				if (MidiPlayer.loadingInstrumentCallback) {
					MidiPlayer.loadingInstrumentCallback(track.instrumentName, false);
				}
				const loadPromise = (async () => {
					// Use a custom nameToUrl that returns a URL ending with "-cached.js"
					const customNameToUrl = (name: any, sf: any, _: any) => {
						sf = sf === 'FluidR3_GM' ? sf : 'MusyngKite';
						return `${appSettings.SoundfontBucketBaseUrl}${sf}/${name}-cached.js`;
					};
					const instrument = await Soundfont.instrument(
						MidiPlayer.audioContext,
						track.instrumentName,
						{
							soundfont: 'MusyngKite',
							format: 'mp3',
							nameToUrl: customNameToUrl,
						}
					);
					MidiPlayer.instrumentPlayers[track.instrumentName] = instrument;
					if (MidiPlayer.loadingInstrumentCallback) {
						MidiPlayer.loadingInstrumentCallback(track.instrumentName, true);
					}
				})();
				MidiPlayer.instrumentLoadingPromises.set(track.instrumentName, loadPromise);
				await loadPromise;
				MidiPlayer.instrumentLoadingPromises.delete(track.instrumentName);
			}
			if (mySessionId !== MidiPlayer.sessionId) {
				return false;
			}
		}
		MidiPlayer.tracks.push(track);
		return true;
	}

	static async preloadInstrument(instrumentName: InstrumentName) {
		if (instrumentName !== ("drumset" as InstrumentName) && !MidiPlayer.instrumentPlayers[instrumentName]) {
			const loadPromise = (async () => {
				// Use a custom nameToUrl that returns a URL ending with "-cached.js"
				const customNameToUrl = (name: any, sf: any, _: any) => {
					sf = sf === 'FluidR3_GM' ? sf : 'MusyngKite';
					return `${appSettings.SoundfontBucketBaseUrl}${sf}/${name}-cached.js`;
				};
				const instrument = await Soundfont.instrument(
					MidiPlayer.audioContext,
					instrumentName,
					{
						soundfont: 'MusyngKite',
						format: 'mp3',
						nameToUrl: customNameToUrl,
					}
				);
				MidiPlayer.instrumentPlayers[instrumentName] = instrument;
			})();
			MidiPlayer.instrumentLoadingPromises.set(instrumentName, loadPromise);
			await loadPromise;
			MidiPlayer.instrumentLoadingPromises.delete(instrumentName);
		}
	}

	static async removeTrack(trackId: string) {
		MidiPlayer.tracks = MidiPlayer.tracks.filter(track => track.trackId !== trackId);
		MidiPlayer.removeTrackCallback(trackId);
	}

	static getTrackCount() {
		return MidiPlayer.tracks.length;
	}

	static stop() {
		MidiPlayer.sessionId++;
		MidiPlayer.tracks.forEach(track => {
			const player = MidiPlayer.instrumentPlayers[track.instrumentName];
			if (player) {
				player.stop();
			}
		});
		MidiPlayer.tracks = [];
		MidiPlayer.playingNoteIds.forEach(noteId => {
			const noteElements = document.querySelectorAll(`.note-${noteId}`);
			noteElements.forEach((element: Element) => {
				const elementY = element.getAttribute('y');
				if (elementY) {
					const y = parseFloat(elementY);
					element.setAttribute('y', (y - noteBounceOffset).toString());
				}
				element.setAttribute('fill', 'black');
			});
		});
		MidiPlayer.playingNoteIds.clear();
		MidiPlayer.isPlaying = false;
		clearInterval(MidiPlayer.intervalId);
		if (MidiPlayer.stopCallback) {
			MidiPlayer.stopCallback();
		}
	}

	private static playDrumSound(note: MidiNote) {
		let buffer: AudioBuffer | null = null;
		switch (note.midi) {
			case 35:
			case 36:
				buffer = MidiPlayer.kickBuffer;
				break;
			case 37:
			case 38:
			case 39:
			case 40:
				buffer = MidiPlayer.snareBuffer;
				break;
			case 42:
			case 44:
			case 54:
			case 56:
			case 67:
			case 68:
			case 69:
			case 70:
			case 73:
			case 75:
			case 76:
			case 77:
			case 80:
				buffer = MidiPlayer.closedHatBuffer;
				break;
			case 46:
			case 58:
			case 74:
			case 81:
				buffer = MidiPlayer.openHatBuffer;
				break;
			case 49:
			case 52:
			case 55:
			case 57:
				buffer = MidiPlayer.crashBuffer;
				break;
			case 51:
			case 53:
			case 59:
				buffer = MidiPlayer.rideBuffer;
				break;
			case 41:
			case 43:
			case 45:
			case 47:
			case 48:
			case 50:
			case 60:
			case 61:
			case 62:
			case 63:
			case 64:
			case 65:
			case 66:
			case 78:
			case 79:
				buffer = MidiPlayer.floorTomBuffer;
				break;
			default:
				break;
		}
		if (buffer) {
			const source = MidiPlayer.audioContext.createBufferSource();
			const gainNode = MidiPlayer.audioContext.createGain();
			gainNode.gain.value = note.velocity * 0.1;
			source.buffer = buffer;
			source.connect(gainNode);
			gainNode.connect(MidiPlayer.audioContext.destination);
			source.start();
		}
	}
}

MidiPlayer.initialize();

export default MidiPlayer;
